import { z } from "zod";
import { agentNozzleStatusSchema } from "../agents/enums.js";
import { alarmSeveritySchema, alarmStatusSchema } from "../alarms/enums.js";
import { BUSINESS_UNIT_TYPES, businessUnitTypeSchema } from "../auth/features.js";
import { localDateSchema, moneySchema, quantitySchema, rateSchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { evConnectorStatusSchema } from "../ev/enums.js";
import { orderUrgencySchema } from "../fuel/forecast.js";
/**
 * Özet (pano) uçları — Prompt 21'in mobil ekranları için MİNİMAL hâli (ADR 0021 §11); Prompt 22 önceden hesaplanmış özetlerle
 * genişletir, sözleşme korunur. Her bölüm kullanıcının izni ve kapsamıyla süzülür: izni olmayan bölüm `null` döner (403 DEĞİL),
 * böylece tek istek bütün ekranı doldurur. Tutarlar kuruş dizgesi (KDV dahil), litreler 3 ondalıklı dizge, değişimler 1 ondalıklı
 * işaretli yüzde dizgesi (karşılaştırılan 0 ise null — gösterim `formatPercentChange`).
 */
const changeSchema = z
    .string()
    .nullable()
    .describe('Yüzde değişim (1 ondalık, işaretli, ör. "-12.5"); karşılaştırılan değer 0 ise null');
export const DASHBOARD_SALES_UNITS = [...BUSINESS_UNIT_TYPES, "OTHER"];
export const dashboardSalesUnitSchema = z.enum(DASHBOARD_SALES_UNITS);
export const salesFigureSchema = z.object({
    revenue: moneySchema,
    /** Akaryakıt + LPG pompa satışı (test hariç). */
    litres: quantitySchema,
});
export const salesUnitRowSchema = z.object({
    unit: dashboardSalesUnitSchema,
    revenue: moneySchema,
    /** Yalnızca pompa satışı olan birimlerde (FUEL, LPG); diğerlerinde null. */
    litres: quantitySchema.nullable(),
    /** Karşılaştırma penceresindeki (dün aynı saate kadar / önceki dönem) ciro. */
    previousRevenue: moneySchema,
    revenueChange: changeSchema,
});
/**
 * Bugünün satışı: [gün başı, şimdi) ↔ dünün ve geçen haftanın aynı gününün AYNI SAATİNE kadar olan penceresi (yarım gün tam
 * günle karşılaştırılmaz). `byUnit.previousRevenue` DÜNE göredir.
 */
export const todaySalesSchema = z.object({
    current: salesFigureSchema,
    yesterday: salesFigureSchema,
    lastWeek: salesFigureSchema,
    revenueChangeVsYesterday: changeSchema,
    revenueChangeVsLastWeek: changeSchema,
    litresChangeVsYesterday: changeSchema,
    litresChangeVsLastWeek: changeSchema,
    byUnit: z.array(salesUnitRowSchema),
});
export const dashboardAlarmItemSchema = z.object({
    id: uuidSchema,
    title: z.string(),
    severity: alarmSeveritySchema,
    status: alarmStatusSchema,
    stationId: uuidSchema.nullable(),
    stationCode: z.string().nullable(),
    lastOccurredAt: timestampSchema,
});
export const dashboardAlarmsSchema = z.object({
    critical: z.number().int().nonnegative(),
    important: z.number().int().nonnegative(),
    info: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    /** Önem sonra son oluşma sırasıyla en önemli açık alarmlar (en çok 5). */
    top: z.array(dashboardAlarmItemSchema),
});
export const STATION_CONNECTION_STATUSES = ["ONLINE", "OFFLINE", "NO_AGENT"];
export const stationConnectionStatusSchema = z.enum(STATION_CONNECTION_STATUSES);
export const STATION_CONNECTION_STATUS_LABELS = {
    ONLINE: "Bağlı",
    OFFLINE: "Bağlantı yok",
    NO_AGENT: "Ajan kurulu değil",
};
export const stationConnectionSchema = z.object({
    status: stationConnectionStatusSchema,
    /** Ajanın son heartbeat'i (ajan yoksa null). */
    lastSeenAt: timestampSchema.nullable(),
});
export const dashboardStationSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    city: z.string().nullable(),
    district: z.string().nullable(),
    latitude: z.string().nullable(),
    longitude: z.string().nullable(),
    businessUnits: z.array(businessUnitTypeSchema),
    /** Bugünkü satış (null: kullanıcının satış izni yok). */
    sales: salesFigureSchema.nullable(),
    /** Geçen haftanın aynı gününün aynı saatine göre ciro değişimi. */
    revenueChangeVsLastWeek: changeSchema,
    connection: stationConnectionSchema.nullable(),
    /** Açık alarm sayıları (null: alarm izni yok). */
    alarms: z
        .object({
        critical: z.number().int().nonnegative(),
        important: z.number().int().nonnegative(),
    })
        .nullable(),
    /** Açık vardiya var mı (null: vardiya izni yok ya da akaryakıt birimi yok). */
    openShift: z.boolean().nullable(),
    /** Sorun rozeti: bağlantı yok ya da açık kritik alarm var. */
    hasIssue: z.boolean(),
});
// ── GET /dashboard/today ─────────────────────────────────────────────────────
export const dashboardTodaySchema = z.object({
    date: localDateSchema,
    asOf: timestampSchema,
    /** Kullanıcının görebildiği istasyon sayısı. */
    stationCount: z.number().int().nonnegative(),
    sales: todaySalesSchema.nullable(),
    alarms: dashboardAlarmsSchema.nullable(),
    approvals: z.object({ pending: z.number().int().nonnegative() }).nullable(),
    stations: z
        .object({
        total: z.number().int().nonnegative(),
        online: z.number().int().nonnegative(),
        offline: z.number().int().nonnegative(),
        noAgent: z.number().int().nonnegative(),
        withIssues: z.number().int().nonnegative(),
    })
        .nullable(),
    /** Kullanıcının görebildiği TEK istasyon varsa (tek istasyonlu kiracı / istasyon müdürü) doğrudan istasyon özeti. */
    station: dashboardStationSchema.nullable(),
});
// ── GET /dashboard/stations ──────────────────────────────────────────────────
export const dashboardStationListSchema = z.object({
    date: localDateSchema,
    asOf: timestampSchema,
    items: z.array(dashboardStationSchema),
});
// ── GET /dashboard/stations/:stationId ───────────────────────────────────────
export const dashboardStationParamsSchema = z.object({ stationId: uuidSchema });
export const dashboardTankSchema = z.object({
    tankId: uuidSchema,
    tankNo: z.number().int(),
    productName: z.string(),
    productColor: z.string().nullable(),
    isLpg: z.boolean(),
    capacityL: quantitySchema,
    volumeL: quantitySchema.nullable(),
    fillPercent: rateSchema.nullable(),
    minLevelL: quantitySchema,
    criticalLevelL: quantitySchema,
    belowMinLevel: z.boolean(),
    belowCriticalLevel: z.boolean(),
    aboveSafeFill: z.boolean(),
    lastReadingAt: timestampSchema.nullable(),
    /** Tüketim profilinden tahmini bitiş (ufukta bitmiyorsa null). */
    estimatedRunOutAt: timestampSchema.nullable(),
});
export const DASHBOARD_NOZZLE_STATUSES = [...agentNozzleStatusSchema.options, "UNKNOWN"];
export const dashboardNozzleStatusSchema = z.enum(DASHBOARD_NOZZLE_STATUSES);
export const dashboardPumpSchema = z.object({
    dispenserId: uuidSchema,
    dispenserNo: z.number().int(),
    nozzles: z.array(z.object({
        nozzleId: uuidSchema,
        nozzleNo: z.number().int(),
        productName: z.string(),
        /** Ajanın bildirdiği son durum; ajan / bildirim yoksa UNKNOWN. */
        status: dashboardNozzleStatusSchema,
        statusAt: timestampSchema.nullable(),
    })),
});
export const dashboardChargerSchema = z.object({
    chargerId: uuidSchema,
    name: z.string(),
    online: z.boolean(),
    connectors: z.array(z.object({
        connectorNo: z.number().int(),
        status: evConnectorStatusSchema.nullable(),
        /** Aktif oturumun anlık gücü (kW, 1 ondalık); oturum yoksa null. */
        powerKw: z.string().nullable(),
    })),
});
export const dashboardWashSchema = z.object({
    /** Sıradaki (bekleyen) iş emirleri / araçlar. */
    waiting: z.number().int().nonnegative(),
    inProgress: z.number().int().nonnegative(),
    /** Bugün tamamlanan. */
    completedToday: z.number().int().nonnegative(),
    /** Arızalı ünite sayısı. */
    unitsInFault: z.number().int().nonnegative(),
});
export const dashboardShiftSchema = z.object({
    open: z
        .object({
        shiftId: uuidSchema,
        name: z.string(),
        openedAt: timestampSchema,
        /** Vardiyadaki personel (ad + görev); kişisel iletişim bilgisi YOK. */
        staff: z.array(z.object({ name: z.string(), role: z.string() })),
    })
        .nullable(),
    /** Son kapanan vardiyanın kapanış zamanı. */
    lastClosedAt: timestampSchema.nullable(),
    /** Gün sonu yapılmamış geçmiş iş günü sayısı. */
    unclosedDays: z.number().int().nonnegative(),
});
export const dashboardCashSchema = z.object({
    total: moneySchema,
    registers: z.array(z.object({ id: uuidSchema, name: z.string(), balance: moneySchema })),
});
export const dashboardStationDetailSchema = z.object({
    date: localDateSchema,
    asOf: timestampSchema,
    station: dashboardStationSchema,
    companyId: uuidSchema,
    sales: todaySalesSchema.nullable(),
    tanks: z.array(dashboardTankSchema).nullable(),
    pumps: z.array(dashboardPumpSchema).nullable(),
    chargers: z.array(dashboardChargerSchema).nullable(),
    wash: dashboardWashSchema.nullable(),
    shift: dashboardShiftSchema.nullable(),
    cash: dashboardCashSchema.nullable(),
    alarms: z.array(dashboardAlarmItemSchema).nullable(),
    /** Akaryakıt sipariş önerileri (yalnızca acil / yakında; `fuel.order.manage`). */
    orderSuggestions: z
        .array(z.object({
        productId: uuidSchema,
        productName: z.string(),
        urgency: orderUrgencySchema,
        suggestedL: quantitySchema,
        earliestRunOutAt: timestampSchema.nullable(),
    }))
        .nullable(),
});
// ── GET /dashboard/finance ───────────────────────────────────────────────────
export const dashboardFinanceQuerySchema = z.object({
    companyId: uuidSchema.optional().describe("Boş = görülebilen tüm şirketler"),
});
const countAmountSchema = z.object({ count: z.number().int().nonnegative(), amount: moneySchema });
export const INSTRUMENT_DIRECTIONS_DASHBOARD = ["RECEIVABLE", "PAYABLE"];
export const dashboardInstrumentDirectionSchema = z.enum(INSTRUMENT_DIRECTIONS_DASHBOARD);
export const dashboardFinanceSchema = z.object({
    date: localDateSchema,
    asOf: timestampSchema,
    companyId: uuidSchema.nullable(),
    /** Nakit pozisyonu: kasalar + banka hesapları (KMH dahil DEĞİL). */
    cash: z
        .object({
        total: moneySchema,
        cashRegisters: moneySchema,
        bankAccounts: moneySchema,
        /** Kullanılabilir KMH limiti toplamı. */
        overdraftAvailable: moneySchema,
    })
        .nullable(),
    /** POS'ta bekleyen (bankaya henüz geçmemiş) bloke tutarları. */
    pos: z.object({ pending: moneySchema, dueToday: moneySchema, overdue: moneySchema }).nullable(),
    cheques: z
        .object({
        receivableToday: countAmountSchema,
        receivableThisWeek: countAmountSchema,
        payableToday: countAmountSchema,
        payableThisWeek: countAmountSchema,
        /** Bu hafta vadesi gelen ilk kalemler (vade sırasıyla, en çok 10). */
        upcoming: z.array(z.object({
            id: uuidSchema,
            direction: dashboardInstrumentDirectionSchema,
            dueDate: localDateSchema,
            amount: moneySchema,
            /** Keşideci / lehtar ÜNVANI (bireysel kişide "Bireysel"). */
            party: z.string(),
            serialNo: z.string().nullable(),
        })),
    })
        .nullable(),
    /** Vadesi geçmiş alacaklar (açık kalemler). */
    receivables: z
        .object({
        overdueTotal: moneySchema,
        overdueCount: z.number().int().nonnegative(),
        top: z.array(z.object({
            counterpartyId: uuidSchema,
            /** Cari ÜNVANI (bireysel cari "Bireysel cari"). */
            name: z.string(),
            amount: moneySchema,
            oldestDueDate: localDateSchema,
            daysOverdue: z.number().int().nonnegative(),
        })),
    })
        .nullable(),
});
// ── GET /dashboard/reports ───────────────────────────────────────────────────
export const DASHBOARD_REPORT_PERIODS = ["DAY", "WEEK", "MONTH"];
export const dashboardReportPeriodSchema = z.enum(DASHBOARD_REPORT_PERIODS);
export const DASHBOARD_REPORT_PERIOD_LABELS = {
    DAY: "Gün",
    WEEK: "Hafta",
    MONTH: "Ay",
};
export const dashboardReportsQuerySchema = z.object({
    period: dashboardReportPeriodSchema.default("WEEK"),
    date: localDateSchema.optional().describe("Dönemin içindeki bir gün (boş = bugün, İstanbul)"),
});
export const dashboardReportsSchema = z.object({
    period: dashboardReportPeriodSchema,
    from: localDateSchema,
    /** Dönemin son günü; dönem bugünü içeriyorsa bugün (henüz bitmemiş dönem). */
    to: localDateSchema,
    previousFrom: localDateSchema,
    previousTo: localDateSchema,
    /** Satış izni yoksa bölümler null. */
    totals: salesFigureSchema.nullable(),
    previousTotals: salesFigureSchema.nullable(),
    revenueChange: changeSchema,
    litresChange: changeSchema,
    byUnit: z.array(salesUnitRowSchema).nullable(),
    /** Gün bazında seri (grafik). */
    series: z
        .array(z.object({ date: localDateSchema, revenue: moneySchema, litres: quantitySchema }))
        .nullable(),
    /** İstasyon karşılaştırması (ciroya göre azalan). */
    stations: z
        .array(z.object({
        stationId: uuidSchema,
        code: z.string(),
        name: z.string(),
        revenue: moneySchema,
        litres: quantitySchema,
        previousRevenue: moneySchema,
        revenueChange: changeSchema,
    }))
        .nullable(),
});
//# sourceMappingURL=dashboard.js.map